import { useState, useMemo } from "react";
import {
  createUnit,
  updateUnit,
  useUnitsStore,
} from "../../../../store/admin/unitsStore";
import { useUnitsList } from "../../../../features/units/queries";
import { useInvalidateDomain } from "../../../../lib/invalidation";
import { getError } from "../../../../utils/getError";
import { useDarkMode } from "../../../../context/ThemeContext";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { getCustomSelectStyles, FilterPopover, DENSE_LIST } from "../../shared";
import { useForm } from "react-hook-form";
import type { Unit, UnitDto } from "../../../../store/admin/unitsStore";
import { useAlert } from "../../../../context/AlertContext";
import { useDebounce } from "../../../../hooks/useDebounce";
import { useDisableCRUD } from "../../../../store/admin/companiesStore";
import { CRUDTooltip } from "../../../../components/base/CRUDTooltip";
import {
  UnitCard,
  UnitListRow,
  AddUnitModal,
  UnitDetailsModal,
  EditUnitModal,
} from "./units";
import TrailerConnectionModal from "./units/modals/TrailerConnectionModal";
import { useDevicesStore } from "../../../../store/admin/devicesStore";
import { useStatusesStore } from "../../../../store/admin/statusesStore";
import {
  getStatusOptionsForEntity,
  getStatusHumanLabel,
} from "../../../../utils/getStatusEntity";
import useIsMonitor from "../../../../hooks/useIsMonitor";
import { useIsReseller } from "../../../../hooks/useIsReseller";
import DeviceConnectionModal from "./units/modals/DeviceConnectionModal";
import PaginationControls from "../../../../components/base/PaginationControls";
import { fuelCapacityField } from "./units/fuelCapacity";

export default function UnitsSection() {
  const { isDarkMode } = useDarkMode();

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showModalDetails, setShowModalDetails] = useState(false);
  const [showTrailerModal, setShowTrailerModal] = useState(false);
  const [trailerUnit, setTrailerUnit] = useState<Unit | null>(null);
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [deviceUnit, setDeviceUnit] = useState<Unit | null>(null);

  // Lista compacta por defecto: es la vista que se escanea más rápido.
  const [selectedView, setSelectedView] = useState("list");
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedType, setSelectedType] = useState("all");

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const debouncedSearch = useDebounce(search);

  const filterParams = useMemo(
    () => ({
      page: currentPage,
      limit: itemsPerPage,
      status: selectedStatus,
      type: selectedType,
      search: debouncedSearch,
    }),
    [currentPage, itemsPerPage, selectedStatus, selectedType, debouncedSearch],
  );

  const unitsQuery = useUnitsList(filterParams);
  const units = unitsQuery.data?.data ?? [];
  const meta = unitsQuery.data?.meta;
  const loading = unitsQuery.isLoading;
  const error = unitsQuery.error ? getError(unitsQuery.error) : null;

  const invalidateDomain = useInvalidateDomain();
  const invalidateUnits = () => invalidateDomain("vehicles");

  const connectOrDisconnectTrailerOnVehicle = useUnitsStore(
    (state) => state.connectOrDisconnectTrailerOnVehicle,
  );
  const deleteUnit = useUnitsStore((state) => state.deleteUnit);
  const attachVehicleToDevice = useDevicesStore(
    (state) => state.attachVehicleToDevice,
  );
  const detachVehicleFromDevice = useDevicesStore(
    (state) => state.detachVehicleFromDevice,
  );

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const { t } = useTranslation("adminSections");
  const lang = localStorage.getItem("lang") || "es";
  const disableCRUD = useDisableCRUD();
  const { isMonitor, isReadOnly } = useIsMonitor();
  const { isReseller } = useIsReseller();
  const totalPages = meta?.totalPages || 1;
  const { showAlert } = useAlert();

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
  };

  // Las units ahora vienen del store y la API

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "text-primary-500 bg-primary-500/10";
      case "INACTIVE":
        return "text-red-500 bg-red-500/10";
      case "MAINTENANCE":
        return "text-yellow-500 bg-yellow-500/10";
      case "OUT_OF_SERVICE":
        return "text-red-500 bg-red-500/10";
      default:
        return "text-gray-500 bg-gray-500/10";
    }
  };

  const getStatusText = (status: string) => {
    if (!status) return t("units.status.unknown");
    return getStatusHumanLabel(status, t);
  };

  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<UnitDto>({
    mode: "onBlur",
    defaultValues: {
      lang: lang,
      name: "",
      plate: "",
      brand: "",
      model: "",
      year: undefined,
      type: "",
      fuelType: "",
      fuelCapacity: undefined,
      icon: "icon",
      status_id: "",
      policy_id: "",
      policy_expiry_date: "",
    },
  });

  const handleSelectUnit = (unit: Unit) => {
    setSelectedUnit(unit);
    setValue("name", unit.name);
    setValue("plate", unit.plate || "");
    setValue("brand", unit.brand || "");
    setValue("model", unit.model || "");
    setValue("year", unit.year ?? undefined);
    setValue("type", unit.type || "");
    setValue("fuelType", unit.fuelType || "");
    setValue("fuelCapacity", fuelCapacityField(unit.fuelCapacity));
    setValue("icon", unit.icon || "icon");
    setValue("status_id", unit.status_id || "");
    setValue("policy_id", unit.policy_id || "");
    setValue("policy_expiry_date", unit.policy_expiry_date || "");
  };

  const types = [
    { value: "car", label: t("units.type.car") },
    { value: "truck", label: t("units.type.truck") },
    { value: "trailer", label: t("units.type.trailer") },
    { value: "pickup", label: t("units.type.pickup") },
    { value: "van", label: t("units.type.van") },
    { value: "suv", label: t("units.type.suv") },
    { value: "mpv", label: t("units.type.mpv") },
    { value: "wagon", label: t("units.type.wagon") },
    { value: "coupe", label: t("units.type.coupe") },
    { value: "convertible", label: t("units.type.convertible") },
    { value: "hatchback", label: t("units.type.hatchback") },
    { value: "sedan", label: t("units.type.sedan") },
    { value: "other", label: t("units.type.other") },
  ];

  const fuelTypes = [
    { value: "gasoline", label: t("units.fields.gasoline") },
    { value: "diesel", label: t("units.fields.diesel") },
    { value: "electric", label: t("units.fields.electric") },
    { value: "hybrid", label: t("units.fields.hybrid") },
    { value: "lpg", label: t("units.fields.lpg") },
    { value: "cng", label: t("units.fields.cng") },
  ];

  const statuses = useStatusesStore((state) => state.statuses);
  const statusesOptions = getStatusOptionsForEntity(statuses, "units", t);

  const onSubmit = async (data: UnitDto) => {
    setLoadingSubmit(true);

    // Eliminar del payload cualquier campo que el usuario no haya llenado
    // (undefined, null, "", NaN). Conservamos `false` y `0` válidos.
    const cleaned = Object.fromEntries(
      Object.entries(data).filter(([, value]) => {
        if (value === undefined || value === null) return false;
        if (typeof value === "string" && value.trim() === "") return false;
        if (typeof value === "number" && Number.isNaN(value)) return false;
        return true;
      }),
    ) as UnitDto;

    if (selectedUnit) {
      await updateUnit(
        selectedUnit.id,
        cleaned,
        showAlert,
        setLoadingSubmit,
        t,
        () => setShowEditModal(false),
        reset,
      );
    } else {
      await createUnit(
        cleaned,
        showAlert,
        setLoadingSubmit,
        t,
        () => setShowAddModal(false),
        reset,
      );
    }
    invalidateUnits();
  };

  const handleDeleteUnit = (unit: Unit) => {
    showAlert({
      title: t("units.delete.confirmTitle", "¿Eliminar unidad?"),
      text: t("units.delete.confirmText", {
        name: unit.name || unit.plate || "",
        defaultValue: "Esta acción marcará la unidad como eliminada.",
      }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("units.delete.confirm", "Eliminar"),
      cancelButtonText: t("units.form.cancel", "Cancelar"),
      confirmButtonColor: "#ef4444",
      onConfirm: async () => {
        const result = await deleteUnit(unit.id);
        if (result.success) {
          invalidateUnits();
          showAlert({
            title: t("units.delete.successTitle", "Éxito"),
            text: t("units.delete.success", "Unidad eliminada correctamente"),
            icon: "success",
            confirmButtonText: "OK",
            confirmButtonColor: "#2563eb",
            onConfirm: () => { },
          });
        } else {
          showAlert({
            title: t("units.form.error"),
            text: result.error || t("units.delete.error", "No se pudo eliminar"),
            icon: "error",
            confirmButtonText: "OK",
            confirmButtonColor: "#2563eb",
            onConfirm: () => { },
          });
        }
      },
    });
  };

  const handleTrailerClick = (unit: Unit) => {
    setTrailerUnit(unit);
    setShowTrailerModal(true);
  };

  const handleDeviceClick = (unit: Unit) => {
    setDeviceUnit(unit);
    setShowDeviceModal(true);
  };

  const handleConnectDevice = async (deviceId: string) => {
    if (!deviceUnit) return;
    const result = await attachVehicleToDevice(deviceId, deviceUnit.id);
    if (result?.success) {
      showAlert({
        title: t("units.device.connectSuccess"),
        text: t("units.device.connectSuccess"),
        icon: "success",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        onConfirm: () => {
          invalidateUnits();
        },
      });
    } else {
      showAlert({
        title: t("units.form.error"),
        text: result?.error || t("units.device.connectError"),
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        onConfirm: () => { },
      });
    }
  };

  const handleDisconnectDevice = async (deviceId: string) => {
    const result = await detachVehicleFromDevice(deviceId);
    if (result?.success) {
      showAlert({
        title: t("units.device.disconnectSuccess"),
        text: t("units.device.disconnectSuccess"),
        icon: "success",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        onConfirm: () => {
          invalidateUnits();
        },
      });
    } else {
      showAlert({
        title: t("units.form.error"),
        text: result?.error || t("units.device.disconnectError"),
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        onConfirm: () => { },
      });
    }
  };

  const handleConnectTrailer = async (trailerId: string) => {
    if (!trailerUnit) return;
    const result = await connectOrDisconnectTrailerOnVehicle(
      trailerUnit.id,
      trailerId,
    );
    if (result?.success) {
      invalidateUnits();
      showAlert({
        title: t("units.trailer.successTitle"),
        text: t("units.trailer.connectedSuccess"),
        icon: "success",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        onConfirm: () => { },
      });
    } else {
      showAlert({
        title: t("units.trailer.errorTitle"),
        text: result?.error || t("units.trailer.connectionError"),
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        onConfirm: () => { },
      });
    }
  };

  const handleDisconnectTrailer = async () => {
    if (!trailerUnit?.remolque_id) return;
    const result = await connectOrDisconnectTrailerOnVehicle(
      trailerUnit.id,
      trailerUnit.remolque_id,
    );
    if (result?.success) {
      invalidateUnits();
      showAlert({
        title: t("units.trailer.successTitle"),
        text: t("units.trailer.disconnectedSuccess"),
        icon: "success",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        onConfirm: () => { },
      });
    } else {
      showAlert({
        title: t("units.trailer.errorTitle"),
        text: result?.error || t("units.trailer.disconnectionError"),
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        onConfirm: () => { },
      });
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div
        className={`${themeClasses.bg} border-b ${themeClasses.border} px-4 py-3 sm:px-6 sm:py-4`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-3">
          <div className="min-w-0">
            <h1
              className={`text-xl sm:text-2xl font-bold ${themeClasses.text}`}
            >
              {t("units.title")}
            </h1>
            <p className={`text-sm ${themeClasses.textSecondary} mt-1`}>
              {t("units.subtitle")}
            </p>
          </div>

          {!isMonitor && !isReadOnly && (
            <CRUDTooltip className="w-full sm:w-auto sm:self-auto flex-shrink-0">
              <button
                onClick={() => setShowAddModal(true)}
                className="w-full sm:w-auto bg-primary-500 hover:bg-primary-400 text-gray-900 px-3 sm:px-4 py-2 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors whitespace-nowrap disabled:opacity-80 disabled:cursor-not-allowed text-sm sm:text-base"
                disabled={disableCRUD}
              >
                <i className="ri-add-line"></i>
                <span className="hidden sm:inline">{t("units.addUnit")}</span>
                <span className="sm:hidden">Nueva</span>
              </button>
            </CRUDTooltip>
          )}
        </div>

        {/* Filters and Controls */}
        <div className="flex items-center gap-2 min-w-0">
          {/* Search */}
          <div className="relative flex-1 min-w-0 sm:flex-none sm:w-72">
            <i
              className={`ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 ${themeClasses.textSecondary} text-sm`}
            ></i>
            <input
              type="text"
              placeholder={t("units.searchPlaceholder")}
              className={`pl-10 pr-3 py-2 h-9 ${isDarkMode ? "bg-dark-card" : "bg-gray-50"
                } border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text
                } placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm w-full`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <FilterPopover
            isDarkMode={isDarkMode}
            activeCount={
              (selectedStatus !== "all" ? 1 : 0) +
              (selectedType !== "all" ? 1 : 0)
            }
            onClear={() => {
              setSelectedStatus("all");
              setSelectedType("all");
              setCurrentPage(1);
            }}
          >
            {/* Status Filter */}
            <Select
              options={statusesOptions}
              styles={getCustomSelectStyles(isDarkMode)}
              placeholder={t("units.status.allUnits")}
              value={
                statusesOptions.find(
                  (status: any) => status.value === selectedStatus,
                ) || null
              }
              onChange={(option: any) => {
                setSelectedStatus(option?.value || "all");
                setCurrentPage(1);
              }}
              isSearchable
              isClearable
              noOptionsMessage={() => t("units.noStatusesAvailable")}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />

            {/* Type Filter */}
            <Select
              options={types}
              styles={getCustomSelectStyles(isDarkMode)}
              placeholder={t("units.status.allTypes")}
              value={
                types.find((type) => type.value === selectedType) || null
              }
              onChange={(option: any) => {
                setSelectedType(option?.value || "all");
                setCurrentPage(1);
              }}
              isSearchable
              isClearable
              noOptionsMessage={() => t("units.noTypesAvailable")}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </FilterPopover>

          {/* View Toggle */}
          <div className="ml-auto flex shrink-0">
            <div
              className={`flex ${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-lg p-1`}
            >
              <button
                className={`px-3 py-1 rounded-md transition-colors ${selectedView === "grid"
                    ? "bg-primary-500 text-gray-900"
                    : `${themeClasses.buttonText} ${themeClasses.buttonHover}`
                  }`}
                onClick={() => setSelectedView("grid")}
                aria-label="Cuadrícula"
                title="Cuadrícula"
              >
                <i className="ri-grid-fill"></i>
              </button>
              <button
                className={`px-3 py-1 rounded-md transition-colors ${selectedView === "list"
                    ? "bg-primary-500 text-gray-900"
                    : `${themeClasses.buttonText} ${themeClasses.buttonHover}`
                  }`}
                onClick={() => setSelectedView("list")}
                aria-label="Lista"
                title="Lista"
              >
                <i className="ri-list-check"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden min-w-0 p-3 sm:p-6">
        {loading ? (
          <div className="text-center text-gray-500">{t("units.loading")}</div>
        ) : error ? (
          <div className="text-center text-red-500">{error}</div>
        ) : selectedView === "grid" ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {units.length > 0 ? (
                units.map((unit) => (
                  <UnitCard
                    key={unit.id}
                    unit={unit}
                    themeClasses={themeClasses}
                    isDarkMode={isDarkMode}
                    getStatusColor={getStatusColor}
                    getStatusText={getStatusText}
                    onEdit={(unit) => {
                      handleSelectUnit(unit);
                      setShowEditModal(true);
                    }}
                    onDetails={(unit) => {
                      handleSelectUnit(unit);
                      setShowModalDetails(true);
                    }}
                    onTrailerClick={handleTrailerClick}
                    onDeviceClick={handleDeviceClick}
                    onDelete={isReseller ? handleDeleteUnit : undefined}
                    disableCRUD={disableCRUD}
                  />
                ))
              ) : (
                <div className="flex justify-center items-center w-full col-span-full">
                  <p className="text-gray-500 text-sm font-bold">
                    No se encontraron unidades
                  </p>
                </div>
              )}
            </div>
            {totalPages > 1 && (
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        ) : (
          <>
            <div className={DENSE_LIST}>
              {units.length > 0 ? (
                units.map((unit) => (
                  <UnitListRow
                    key={unit.id}
                    unit={unit}
                    themeClasses={themeClasses}
                    getStatusColor={getStatusColor}
                    getStatusText={getStatusText}
                    onEdit={(unit) => {
                      handleSelectUnit(unit);
                      setShowEditModal(true);
                    }}
                    onDetails={(unit) => {
                      handleSelectUnit(unit);
                      setShowModalDetails(true);
                    }}
                    onTrailerClick={handleTrailerClick}
                    onDeviceClick={handleDeviceClick}
                    onDelete={isReseller ? handleDeleteUnit : undefined}
                    disableCRUD={disableCRUD}
                  />
                ))
              ) : (
                <div className="flex justify-center items-center w-full">
                  <p className="text-gray-500 text-sm font-bold">
                    No se encontraron unidades
                  </p>
                </div>
              )}
            </div>
            {totalPages > 1 && (
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </div>

      {showAddModal && (
        <AddUnitModal
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          form={
            {
              register,
              handleSubmit,
              control,
              setValue,
              reset,
              setError,
              clearErrors,
              formState: { errors },
            } as any
          }
          types={types}
          fuelTypes={fuelTypes}
          loadingSubmit={loadingSubmit}
          onClose={() => {
            setShowAddModal(false);
            setSelectedUnit(null);
            reset();
          }}
          onSubmit={onSubmit}
        />
      )}

      {showModalDetails && selectedUnit && (
        <UnitDetailsModal
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          unit={selectedUnit}
          getStatusText={getStatusText}
          disableCRUD={disableCRUD}
          onClose={() => {
            setShowModalDetails(false);
            setSelectedUnit(null);
            reset();
          }}
          onEdit={() => {
            setShowModalDetails(false);
            setShowEditModal(true);
          }}
        />
      )}

      {showEditModal && selectedUnit && (
        <EditUnitModal
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          unit={selectedUnit}
          form={
            {
              register,
              handleSubmit,
              control,
              setValue,
              reset,
              setError,
              clearErrors,
              formState: { errors },
            } as any
          }
          types={types}
          fuelTypes={fuelTypes}
          loadingSubmit={loadingSubmit}
          onClose={() => {
            setShowEditModal(false);
            setSelectedUnit(null);
            reset();
          }}
          onSubmit={onSubmit}
        />
      )}

      {showTrailerModal && trailerUnit && (
        <TrailerConnectionModal
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          unit={trailerUnit}
          onClose={() => {
            setShowTrailerModal(false);
            setTrailerUnit(null);
          }}
          onConnect={handleConnectTrailer}
          onDisconnect={handleDisconnectTrailer}
        />
      )}

      {showDeviceModal && deviceUnit && (
        <DeviceConnectionModal
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          unit={deviceUnit}
          onClose={() => {
            setShowDeviceModal(false);
            setDeviceUnit(null);
          }}
          onConnect={handleConnectDevice}
          onDisconnect={handleDisconnectDevice}
        />
      )}
    </div>
  );
}
